import { Component, OnInit, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ChatService } from '../../services/chat.service';
import { ChatMessage } from '../../models/chat-message.model';
import { ChatSettingsService, ChatSettings } from '../../services/chat-settings.service';
import { MarkdownPipe } from '../../pipes/markdown.pipe';

@Component({
  selector: 'sc-chat-container',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, MarkdownPipe],
  templateUrl: './chat-container.component.html',
  styleUrls: ['./chat-container.component.css'],
})
export class ChatContainerComponent implements OnInit {
  @Output() closeChat = new EventEmitter<void>();

  @ViewChild('messageInput') messageInputRef!: ElementRef<HTMLTextAreaElement>;

  draft = '';
  messages: ChatMessage[] = [];
  isPending = false;
  showResetConfirmation = false;
  showCloseConfirmation = false;
  showOptions = false;
  exportCopied = false;
  exportError = false;
  options!: ChatSettings; // Will be initialized in ngOnInit from service

  // Flag to require confirmation before closing if true (e.g. if chat is active)
  requiresCloseConfirmation = false;

  constructor(
    private readonly chatService: ChatService,
    private readonly chatSettingsService: ChatSettingsService,
  ) {
    this.chatService.messages$.subscribe(list => {
      this.messages = list;
      this.requiresCloseConfirmation = this.messages.length > 0;
      const wasPending = this.isPending;
      this.isPending = this.messages.some(m => m.pending);
      if (wasPending && !this.isPending) {
        // Defer focus to the next event loop tick so Angular has finished updating
        // the DOM (e.g. re-enabling the textarea) before we attempt to focus it.
        setTimeout(() => this.messageInputRef?.nativeElement.focus());
      }
      setTimeout(() => {
        const container = document.querySelector('.sc-messages');
        if (container) (container as HTMLElement).scrollTop = container.scrollHeight;
      });
    });

    this.chatService.messageError$.subscribe(errorMessageContent => {
      this.draft = errorMessageContent;
    });
  }

  ngOnInit(): void {
    // Load settings from localStorage on component initialization
    this.options = this.chatSettingsService.loadSettings();
  }

  onSend(evt: Event) {
    evt.preventDefault();
    if (this.isPending || !this.draft.trim()) return;
    const content = this.draft.trim();
    this.draft = '';
    this.chatService.sendMessage(content);
    // Defer focus so the ngModel reset (draft = '') is processed by Angular
    // before we attempt to focus, preventing the browser from losing focus
    // due to change detection running after this synchronous call.
    setTimeout(() => this.messageInputRef?.nativeElement.focus());
  }

  promptReset() {
    this.showResetConfirmation = true;
  }

  confirmReset(confirm: boolean) {
    if (confirm) {
      this.chatService.resetConversation();
    }
    this.showResetConfirmation = false;
  }

  openOptions() {
    this.showOptions = true;
  }

  closeOptions() {
    // Save settings to localStorage when options dialog is closed
    this.chatSettingsService.saveSettings(this.options);
    this.showOptions = false;
  }

  close() {
    if (this.requiresCloseConfirmation) {
      this.showCloseConfirmation = true;
    } else {
      this.closeChat.emit();
    }
  }

  confirmClose(confirm: boolean) {
    this.showCloseConfirmation = false;
    if (confirm) {
      this.chatService.resetConversation();
      this.closeChat.emit();
    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey && this.options.sendWithEnter) {
      event.preventDefault();
      this.onSend(event);
    }
  }
  onInput(event: Event) {
    const textarea = event.target as HTMLTextAreaElement;
    const maxHeight = 100; // Has to match with CSS max-height
    textarea.style.height = 'auto'; // reset in order to recalculate scrollHeight
    if (textarea.scrollHeight > maxHeight) {
      textarea.style.height = `${maxHeight}px`;
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.height = `${textarea.scrollHeight}px`;
      textarea.style.overflowY = 'hidden';
    }
  }

  isInputEmpty(): boolean {
    return !this.draft || this.draft.trim().length === 0;
  }

  private formatMessagesAsText(): string {
    return this.messages
      .filter(m => !m.pending)
      .map(m => {
        const time = m.createdAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const role = m.role === 'user' ? 'Sie' : 'myAssist';
        return `[${time}] ${role}:\n${m.content}`;
      })
      .join('\n\n');
  }

  exportToClipboard() {
    const text = this.formatMessagesAsText();
    navigator.clipboard
      .writeText(text)
      .then(() => {
        this.exportCopied = true;
        this.exportError = false;
        setTimeout(() => (this.exportCopied = false), 2500);
      })
      .catch(() => {
        this.exportError = true;
        setTimeout(() => (this.exportError = false), 3000);
      });
  }

  exportToTxt() {
    const text = this.formatMessagesAsText();
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const date = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `myAssist-Chat-${date}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }
}
